/**
 * useNotifiche — hook TanStack Query per le notifiche in-app.
 *
 * Conteggio: polling 30s + refetch onWindowFocus (badge topbar).
 * Lista: refetch on demand.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";

export type FiltroNotifiche = "tutte" | "non_lette" | "lette";

export type PrioritaNotifica = "bassa" | "media" | "alta" | "urgente";

export interface NotificaItem {
  id: string;
  evento_tipo: string;
  titolo: string;
  messaggio: string | null;
  priorita: PrioritaNotifica;
  payload: Record<string, unknown>;
  link_app: string | null;
  letta: boolean;
  letta_at: string | null;
  created_at: string;
}

export interface NotificaListResponse {
  items: NotificaItem[];
  has_more: boolean;
}

export interface ConteggioResponse {
  non_lette: number;
  totali: number;
}

const QK_CONTEGGIO = ["notifiche", "conteggio"] as const;
const qkList = (filtro: FiltroNotifiche, limit: number) =>
  ["notifiche", "list", filtro, limit] as const;

export function useNotificheConteggio() {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: QK_CONTEGGIO,
    queryFn: async () => {
      const res = await api.get<ConteggioResponse>("/notifiche/conteggio");
      return res.data;
    },
    enabled: isAuth,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

export function useNotificheList(
  filtro: FiltroNotifiche = "tutte",
  limit = 50,
) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: qkList(filtro, limit),
    queryFn: async () => {
      const res = await api.get<NotificaListResponse>("/notifiche", {
        params: { filtro, limit },
      });
      return res.data;
    },
    enabled: isAuth,
  });
}

export function useMarkLetta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<NotificaItem>(`/notifiche/${id}/segna-letta`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifiche"] });
    },
  });
}

export function useMarkTutteLette() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<{ aggiornate: number }>(
        "/notifiche/segna-tutte-lette",
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifiche"] });
    },
  });
}
